/**
 * TendersListing component.
 * @module components/Tenders/TendersListing
 */

import React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Header, Grid, Card } from 'semantic-ui-react';
import { BodyClass, flattenToAppURL, hasBlocksData } from '@plone/volto/helpers';
import { UniversalLink, Pagination, FormattedDate } from '@plone/volto/components';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import { isEmpty, map } from 'lodash';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { formatDate } from '../../helpers';
import { Sharing } from '../';


const TendersListing = (props) => {
	const { content } = props;
	const lang = content.language.token;
	const dispatch = useDispatch();
	const intl = useIntl();
	const tenders = useSelector((state) => state.search.subrequests.tenders?.items);
	const total = useSelector((state) => state.search.subrequests.tenders?.total);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(4);

	const vocabularyTenderStatus = 'product.rubi_proursa.vocabularies.TenderStatus';
	const vocabularyTenderTypes = 'product.rubi_proursa.vocabularies.TenderTypes';
	const vocabularyTenderProcedures = 'product.rubi_proursa.vocabularies.TenderProcedures';
	const tenderStatus = useSelector((state) => state.vocabularies[vocabularyTenderStatus]);
	const tenderTypes = useSelector((state) => state.vocabularies[vocabularyTenderTypes]);
	const tenderProcedures = useSelector((state) => state.vocabularies[vocabularyTenderProcedures]);

	const getVocabularyLabel = function (value, vocabulary) {
		if (!vocabulary || !vocabulary.items) {
			return value
		}
		for (let item of vocabulary.items) {
			if (item.value === value) return item.label
		}
		return value
	}

	const getTenderStatusClasses = function (status) {
		const statusClasses = {
			opened: 'green opened',
			closed: 'red closed',
			assigned: 'blue assigned',
			future: 'yellow opened',
		}
		return statusClasses?.[status] || 'grey unknown';
	}

	const getTenderStatus = function (status) {
		return getVocabularyLabel(status, tenderStatus)
	}

	const getTenderType = function (type) {
		return getVocabularyLabel(type, tenderTypes)
	}

	const getTenderProcedure = function (procedure) {
		return getVocabularyLabel(procedure, tenderProcedures)
	}

	const getPublicationDate = function (publicationDate) {
		const format = {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		};
		const locale = lang === 'ca' ? 'ca-ES' : 'es-Es'
		const date = new Date(publicationDate).toLocaleDateString(locale, format);
		return date
	}

	const onChangePage = (event, activePage) => {
		event.preventDefault();
		setCurrentPage(activePage.value);
	};

	const onChangePageSize = (event) => {
		event.preventDefault();
		setPageSize(parseInt(event.target.text));
		setCurrentPage(0);
	};

	useEffect(() => {
		dispatch(getVocabulary({ vocabNameOrURL: vocabularyTenderStatus, lang: content.language.token }));
		dispatch(getVocabulary({ vocabNameOrURL: vocabularyTenderTypes, lang: content.language.token }));
		dispatch(getVocabulary({ vocabNameOrURL: vocabularyTenderProcedures, lang: content.language.token }));
	}, [dispatch]);

	useEffect(() => {
		let contentFilter = {
			portal_type: ['Tender'],
			b_size: pageSize,
			b_start: currentPage * pageSize,
			review_state: 'published',
			sort_on: 'getObjPositionInParent',
			sort_order: 'reverse',
			metadata_fields: [
				'tender_status', 'tender_expedient', 'tender_publication_date',
				'tender_type', 'tender_budget', 'tender_contract_value', 'tender_procedure',
				'remoteUrl',
			],
		};
		dispatch(searchContent(flattenToAppURL(content['@id']), contentFilter, 'tenders'));
	}, [dispatch, currentPage, pageSize]);

	useEffect(() => {
		const divWrapper = document.querySelector('.tendersListing__wrapper');
		const offsetTop = 0
		let scrollPos = window.scrollY;
		if (scrollPos > offsetTop && divWrapper) {
			window.scrollTo({ top: offsetTop })
		}
	}, [currentPage]);

	return (
		<>
			<BodyClass className='tenderslisting' />

			<Container>

				{content.description && (
					<p className="documentDescription">{content.description}</p>
				)}

				{hasBlocksData(content) ? (
					<RenderBlocks {...props} />
				) : null}

				<div className="d-flex justify-content-end mt-4">
					<UniversalLink className="ui primary button" href="https://contractaciopublica.cat/ca/perfils-contractant/detall/9268028?categoria=0">
						<FormattedMessage id="Plataforma de Contractació" defaultMessage="Plataforma de Contractació" />
					</UniversalLink>
				</div>

				{!isEmpty(tenders) ? (
					<>
						<ul className='list-unstyled mb-5'>
							{tenders.map((item) => (
								<li className="mb-4 pb-2" key={item['@id']}>
									<Card fluid as="article" className='tendersItem position-relative'>
										<Card.Content>
											<Card.Header className="d-flex flex-nowrap align-items-start">
												<h2 className='tendersItem__title fsc-h2 mr-auto'>
													{item.remoteUrl ? (
														<UniversalLink
															className="tendersItem__link tendersItem__title stretched-link"
															href={item.remoteUrl}>
															{item.title}
														</UniversalLink>
													) : (
														<span>{item.title}</span>
													)}
												</h2>
												{item.remoteUrl && (<Sharing id={item['@id']} title={item.title} url={item.remoteUrl} />)}
											</Card.Header>
											<Card.Description as="p" className="tendersItem__desc">{item.description}</Card.Description>
											<Grid className='mt-4'>
												<Grid.Row columns={12} as="ul">
													{item.tender_expedient && (
														<Grid.Column as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Expedient" defaultMessage="Expedient" /></strong>
															{item.tender_expedient}
														</Grid.Column>
													)}

													{item.tender_publication_date && (
														<Grid.Column  as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Publication date" defaultMessage="Publication date" /></strong>
															{getPublicationDate(item.tender_publication_date)}
														</Grid.Column>
													)}

													{item.tender_type && (
														<Grid.Column  as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Type" defaultMessage="Type" /></strong>
															{getTenderType(item.tender_type)}
														</Grid.Column>
													)}

													{item.tender_budget && (
														<Grid.Column  as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Budget of tender" defaultMessage="Budget of tender" /></strong>
															{item.tender_budget}
														</Grid.Column>
													)}

													{item.tender_contract_value && (
														<Grid.Column  as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Contract value" defaultMessage="Contract value" /></strong>
															{item.tender_contract_value}
														</Grid.Column>
													)}

													{item.tender_procedure && (
														<Grid.Column  as="li" className="mb-4" mobile={12} tablet={6} computer={6}>
															<strong className='d-block'><FormattedMessage id="Procedure" defaultMessage="Procedure" /></strong>
															{getTenderProcedure(item.tender_procedure)}
														</Grid.Column>
													)}
												</Grid.Row>
											</Grid>

											{item.tender_status && (
												<Card.Meta as="p" className='text-end'>
													<span className={'tendersItem__status ' + (getTenderStatusClasses(item.tender_status))}>{getTenderStatus(item.tender_status)}</span>
												</Card.Meta>
											)}
										</Card.Content>
										
									</Card>
								</li>
							))}
						</ul>

						{total > pageSize && (
							<nav className="pagination">
								<Pagination
									current={currentPage}
									total={Math.ceil(total / pageSize)}
									onChangePage={onChangePage}
									onChangePageSize={onChangePageSize}
								/>
							</nav>
						)}

					</>
				) : (
					<p><FormattedMessage id="No tenders found" defaultMessage="No tenders found" /></p>
				)}

			</Container>

		</>
	);
};


export default TendersListing;
